import {
  ExperienceType,
  PayRequestPayload,
} from "checkout-query-client/api/types";
import { MerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { SepaFieldNames } from "checkout-query-client/types/chex-form-fields";
import {
  FieldValues,
  PaymentType,
} from "checkout-query-client/types/form-types";
import { buildPostalPayload } from "checkout-query-client/utils/build-payloads/build-base-payload";
import {
  getAcceptedMandates,
  getDeviceFingerprintSessionId,
} from "checkout-query-client/utils/helpers";

import { IEncryptData, valueEncrypt } from "encrypt-value/encrypt";

import {
  BrowserInfo,
  EncryptedSepa,
  PayRequest,
  PayResponse_Status,
  PaymentMethod,
  PaymentMethod_Type,
} from "frontend-client";

export const buildSepaPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const {
    formFields,
    merchantSettings,
    checkoutSessionData,
    experienceType,
    orderDigest,
  } = payload;
  const request = new PayRequest();
  const encryptedSepa = new EncryptedSepa();
  const paymentMethod = new PaymentMethod();

  if (formFields && merchantSettings) {
    const billingAddressSameAsShipping =
      formFields?.doAddressesMatch === "true";

    encryptedSepa.billingAddress = buildPostalPayload(
      formFields,
      PaymentType.sepa,
      payload.paymentMethodId ?? ""
    );

    encryptedSepa.billingAddressSameAsShipping = billingAddressSameAsShipping;

    const sepaPayload = await createEncryptedSepaPayload(
      merchantSettings,
      formFields,
      experienceType
    );

    if (sepaPayload.status === PayResponse_Status.SUCCESS && sepaPayload.sepa) {
      encryptedSepa.encryptionKeyId = sepaPayload.sepa.encryptionKeyId;
      encryptedSepa.encryptedBic = sepaPayload.sepa.encryptedBic;
      encryptedSepa.encryptedIban = sepaPayload.sepa.encryptedIban;
    }

    encryptedSepa.shopperEmailId = formFields?.sepaEmail ?? "";

    paymentMethod.sepa = encryptedSepa;
    paymentMethod.type = PaymentMethod_Type.SEPA;
    request.paymentMethod = paymentMethod;
    request.acceptedMandates = getAcceptedMandates(payload.formFields);
    request.billingAddressFromIntentBilling =
      Boolean(checkoutSessionData?.billingAddress) &&
      !merchantSettings?.paymentMethods?.sepaPaymentMethod
        ?.billingAddressCollection?.enabled;

    // 3ds TODO: tie this in
    request.scaChallengeWindowSize = 4;

    const date = new Date();

    request.browserInfo = new BrowserInfo({
      acceptHeader: "text/html;charset=UTF-8",
      language: window.navigator.language || "",
      colorDepth: window.screen.colorDepth || 0,
      screenHeight: window.screen.height || 0,
      screenWidth: window.screen.width || 0,
      timezoneOffset: date.getTimezoneOffset() || 0,
    });
    request.orderDigest = orderDigest ?? "";
    request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();

    // text user consent "agreed" too
    request.consentToStoreForFuturePayments =
      formFields?.collectConsentToStoreForFuturePayments === "true"
        ? formFields?.collectConsentToStoreForFuturePaymentsText || ""
        : "";
    // setting payment method to default mop
    request.makeDefaultPaymentMethod = formFields?.setAsDefault === "true";
    // saving consumer profile based on the merchant intent.
    request.savePaymentMethodToProfile =
      formFields?.collectConsentToStoreForFuturePayments === "true";
  }
  return request;
};

const createEncryptedSepaPayload = async (
  merchantSettings: MerchantSettings,
  formFields: FieldValues<SepaFieldNames>,
  experienceType?: ExperienceType
) => {
  let encryptedIbanData: IEncryptData;
  let encryptedBicData: IEncryptData;

  try {
    if (experienceType === "dropInUI") {
      encryptedIbanData = {
        encryptedData: formFields?.iban || "",
        encryptionNonce: "",
        encryptionTimestamp: 0,
      };
      encryptedBicData = {
        encryptedData: formFields?.bic || "",
        encryptionNonce: "",
        encryptionTimestamp: 0,
      };
    } else {
      encryptedIbanData = await valueEncrypt({
        value: formFields?.iban?.replaceAll(" ", "") ?? "",
        key: merchantSettings?.encryptionKey ?? "",
      });
      encryptedBicData = await valueEncrypt({
        value: formFields?.bic ?? "",
        key: merchantSettings?.encryptionKey ?? "",
      });
    }
    return {
      status: PayResponse_Status.SUCCESS,
      sepa: {
        encryptedIban: encryptedIbanData.encryptedData,
        encryptedBic: encryptedBicData.encryptedData,
        encryptionKeyId: merchantSettings?.encryptionKeyId ?? "",
      },
    };
  } catch (e) {
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.payment,
      message: PubSubMessage.SepaEncryptionError,
    });
    return { status: PayResponse_Status.UNSPECIFIED };
  }
};
